import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import classnames from 'classnames';
import { createTeleporter } from 'react-teleporter';

import { makeStyles } from '@mui/styles';

import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';

import SimpleHeader from 'Modules/main-header/variants/SimpleHeader';
import type { BackCta } from 'Modules/main-header';
import {
  PredictiveCRMNewFeaturePromotion,
  PredictiveCRMPopUp,
} from 'Modules/predictive-crm/components';

import DisclaimersTeleporter from '../../teleporters/DisclaimersTeleporter';

export const BACKGROUNDS = {
  PRIMARY: 'primary',
  DRAWINGS: 'drawings',
  ALTERNATIVE: 'alternative',
  DASHBOARD: 'dashboard',
};

interface Props {
  className?: string;
  children?: ReactNode;
  background?: 'primary' | 'drawings' | 'alternative' | 'dashboard';
  hasSmallLogo?: boolean;
  isWide?: boolean;
  isFixed?: boolean;
  backCta?: BackCta;
}

const useStyles = makeStyles(
  /* istanbul ignore next */ ({ palette }) => ({
    root: {
      minHeight: '100vh',
    },
    rootDrawings: {
      backgroundImage: 'url(/images/backgrounds/bg_oc_things_grey.png)',
    },
    rootAlternative: {
      background: palette.background.alternative,
    },
    rootDashboard: {
      background: palette.background.dashboard,
    },
    rootPrimary: {
      background: palette.background.main,
    },
    header: {
      position: 'relative',
      zIndex: 10,
      borderBottom: `1px solid ${palette.divider}`,
    },
    fixedHeader: {
      position: 'fixed',
      top: 0,
      width: '100%',
    },
  })
);

export const HeadLine = createTeleporter();

const SimpleHeaderScreen = ({
  children = null,
  className = '',
  background = 'drawings',
  hasSmallLogo = false,
  isWide = false,
  isFixed = false,
  backCta,
}: Props) => {
  const classes = useStyles();
  const { currentUser: user } = useCurrentUserContext();

  return (
    <div
      className={classnames([classes.root, className], {
        [classes.rootDrawings]: background === 'drawings',
        [classes.rootAlternative]: background === 'alternative',
        [classes.rootDashboard]: background === 'dashboard',
        [classes.rootPrimary]: background === 'primary',
      })}
    >
      <div
        className={classnames(classes.header, {
          [classes.fixedHeader]: isFixed,
        })}
      >
        <SimpleHeader
          backCta={backCta}
          user={user}
          isWide={isWide}
          headline={<HeadLine.Target />}
          hasSmallLogo={hasSmallLogo}
        />
      </div>
      <DisclaimersTeleporter.Target />
      {children || <Outlet />}
      <PredictiveCRMNewFeaturePromotion />
      <PredictiveCRMPopUp />
    </div>
  );
};

SimpleHeaderScreen.HeadLine = HeadLine;

export default SimpleHeaderScreen;
